/*
 * MyHomeCarousel — personalized quick-book section on the homepage for
 * authenticated customers (NOT staff). Shows:
 *   - Upcoming appointment card (if any)
 *   - "Book again" favorite services chips (top 6)
 *   - "Time for another…" maintenance suggestions
 * Falls back to rendering nothing if user has no history yet.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Clock, CalendarCheck, ArrowRight, RefreshCw, Home, Bell
} from "lucide-react";

export default function MyHomeCarousel({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Gate: authenticated customers only. Staff use the Field Dashboard.
  const isCustomer = user && !user.is_technician && !user.is_admin;

  useEffect(() => {
    if (!isCustomer) { setLoading(false); return; }
    (async () => {
      try {
        const res = await axiosInstance.get("/my-home");
        setData(res.data);
      } catch (_) {}
      setLoading(false);
    })();
  }, [isCustomer]);

  if (!isCustomer || loading || !data) return null;

  const { favorites = [], suggestions = [], upcoming = null, history_count = 0 } = data;

  // Don't render anything for first-time customers — they get the regular hero CTA
  if (!upcoming && favorites.length === 0 && suggestions.length === 0) return null;

  const firstName = (user.full_name || user.email || "").split(" ")[0] || "there";

  const bookAgain = (service_type, label) => {
    const params = new URLSearchParams({ service_type, source: "my_home_rebook" });
    if (label) params.set("service_label", label);
    navigate(`/get-estimate?${params}`);
  };

  return (
    <section
      className="py-10 bg-gradient-to-br from-tenant-primary-soft via-white to-tenant-accent-soft"
      data-testid="my-home-section"
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Home className="w-6 h-6 text-tenant-primary" />
              Welcome back, {firstName}
            </h2>
            <p className="text-sm text-muted-foreground">
              {history_count > 0
                ? `You've booked ${history_count} ${history_count === 1 ? "service" : "services"} with us — here's your quick dashboard.`
                : "Your dashboard."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Upcoming appointment */}
          {upcoming && (
            <Card className="md:col-span-3 border-l-4 border-l-tenant-accent" data-testid="my-home-upcoming">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-tenant-accent-soft flex items-center justify-center">
                    <CalendarCheck className="w-5 h-5 text-tenant-accent" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase font-semibold text-tenant-accent">Upcoming</div>
                    <div className="font-semibold text-gray-900">{upcoming.service_label || upcoming.service_type || "Service"}</div>
                    <div className="text-xs text-gray-500">
                      {upcoming.booking_date ? new Date(upcoming.booking_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "Scheduled"}
                      {upcoming.booking_time ? ` · ${upcoming.booking_time}` : ""}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate("/profile?tab=bookings")} data-testid="my-home-upcoming-details">
                  Details <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Favorites / Book Again */}
          {favorites.length > 0 && (
            <Card className="md:col-span-2" data-testid="my-home-favorites">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-tenant-primary" />
                  <div className="font-semibold text-gray-900">Book again</div>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="my-home-favorites-list">
                  {favorites.map((f) => (
                    <button
                      key={f.service_type}
                      onClick={() => bookAgain(f.service_type, f.label)}
                      className="group flex items-center gap-2 border rounded-lg px-3 py-2 bg-white hover:border-tenant-primary hover:shadow-sm transition text-left"
                      data-testid={`my-home-fav-${f.service_type}`}
                    >
                      <Sparkles className="w-4 h-4 text-tenant-primary flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{f.label}</div>
                        <div className="text-[11px] text-gray-500">
                          Booked {f.count}× · last {f.last_booked_at ? new Date(f.last_booked_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "recently"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Maintenance suggestions */}
          {suggestions.length > 0 && (
            <Card className="md:col-span-1 bg-amber-50/60 border-amber-200" data-testid="my-home-suggestions">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="w-4 h-4 text-amber-600" />
                  <div className="font-semibold text-amber-900">Probably due</div>
                </div>
                <ul className="space-y-2">
                  {suggestions.map((s) => (
                    <li key={s.service_type} className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{s.label}</div>
                        <div className="text-[11px] text-gray-600">{s.reason}</div>
                        <button
                          onClick={() => bookAgain(s.service_type, s.label)}
                          className="text-xs text-tenant-primary font-medium mt-0.5 hover:underline"
                          data-testid={`my-home-sug-${s.service_type}`}
                        >
                          Book it →
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}
